/* Google Tag Manager container + marketing conversion events (OPSS-249).
 *
 * GTM exists here for one job: put two conversion signals — SignupSuccessful and
 * AgentCreated — somewhere marketing can forward them to advertising platforms
 * without further frontend work per platform. Product analytics stays in
 * Amplitude (../analytics/tracking); this is a second, deliberately tiny sink.
 *
 * Two divergences from initAmpli, both intentional:
 *   - No baked fallback container id. initAmpli falls back to ApiKey.default (a
 *     real staging key), so ampli events flow even in `npm run dev`. A marketing
 *     container must never receive developer or CI traffic, so an absent id here
 *     means "do nothing".
 *   - `dataLayer` is created unconditionally, BEFORE the id check. pushEvent runs
 *     from inside the agent-client tracking decorator; a throw there surfaces to
 *     the onboarding wizard as a failed agent creation (after the agent already
 *     exists), so the push must be inert rather than absent. */

/** Injected once per page load; nothing else guards this (unlike the generated
 *  Ampli client, whose load() self-guards). Not a StrictMode concern —
 *  initExternalIntegrations runs at module scope, not in an effect. */
let injected = false;

/** Install the GTM container. No-ops unless VITE_GTM_CONTAINER_ID is a non-empty
 *  string: helm declares env vars it doesn't set, and entrypoint.sh emits those
 *  as "", so an `undefined` check would inject a script with an empty id. */
export function initGtm(): void {
  // Unconditional: pushEvent must stay a safe no-op on every path that returns
  // early below (local dev, mock mode, jest, Playwright — none carry a container id).
  window.dataLayer = window.dataLayer || [];

  const containerId = window.__env__?.VITE_GTM_CONTAINER_ID;
  if (!containerId || containerId.length === 0) return;

  if (injected) return;

  injected = true;

  window.dataLayer.push({
    "gtm.start": Date.now(),
    event: "gtm.js",
  });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId)}`;
  document.head.appendChild(script);

  // The ticket also asked for the <noscript> iframe fallback. Deliberately
  // omitted: this is a JS-only SPA, so a page a user could actually see never
  // renders without JavaScript and the fallback can never fire.
}

/** Push a marketing conversion event. Optional-chained and caught: this runs on
 *  the success path of real user actions (sign-up, agent creation), so a GTM
 *  problem must never propagate into them. Mirrors
 *  platform-auth-ui/src/services/gtm/gtm.service.ts, minus its
 *  eventCallback/Promise.race — that exists to survive a cross-app navigation,
 *  whereas our post-conversion hops are client-side React Router. */
export function pushGtmEvent(
  event: string,
  props: Record<string, string> = {},
): void {
  try {
    window.dataLayer?.push({ event, ...props });
  } catch {
    // Best effort: a dropped marketing event must not break a user flow.
  }
}

/** Test seam: reset the once-per-load injection guard. */
export function resetGtmForTests(): void {
  injected = false;
}
